import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import Avatar from '@material-ui/core/Avatar';
import { useSelector } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import usePermission from 'app/hooks/usePermission';
import { encompassAuthorizeUrl } from 'app/main/utils/encompass';
import InstallAppButton from 'app/fuse-layouts/shared-components/InstallAppButton';

const useStyles = makeStyles(theme => ({
  headernav: {
    background: '#FFF',
    borderBottom: '1px solid #ccc',
    zIndex: 50
  },
  loginIcons: {
    width: '26px',
    '@media screen and (max-width: 959px)': {
      width: '26px'
    }
  },
  avatar: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    fontSize: '1.5em',
    boxSizing: 'content-box',
    left: '10%',
    transform: 'translateX(-50%)',
    transition: theme.transitions.create('all', {
      duration: theme.transitions.duration.shortest,
      easing: theme.transitions.easing.easeInOut
    }),
    '&.initials': {
      width: 72,
      height: 72,
      padding: 5
    },
    '& > img': {
      borderRadius: '50%',
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    '@media screen and (max-width: 959px)': {
      width: 25,
      height: 25,
      marginTop: '10px',
      marginBottom: '10px',
      marginRight: '-14px',
      fontSize: '1em',
      '& > img': {
        objectFit: 'cover'
      }
    }
  },
  loginRow: {
    width: '7.5px',
    height: '7.5px',
    marginLeft: '-5px',
    '@media screen and (max-width: 959px)': {
      width: '7.5px',
      height: '7.5px'
    }
  },
  loginRowB: {
    width: '7.5px',
    height: '7.5px',
    '@media screen and (max-width: 959px)': {
      width: '7.5px',
      height: '7.5px'
    }
  },
  hiddenNav: {
    maxWidth: '200px',
    width: '200px',
    position: 'fixed',
    top: '59px',
    left: '37px',
    paddingLeft: '36px',
    paddingRight: '30px',
    paddingBottom: '20px',
    paddingTop: '20px',
    borderRight: '1px solid #ccc',
    borderBottom: '1px solid #ccc',
    marginLeft: '-37px',
    '@media screen and (max-width: 1279px)': {
      top: '52px'
    },
    '@media screen and (max-width: 959px)': {
      top: '46px',
      left: '36px'
    }
  },
  hiddenNav2: {
    position: 'fixed',
    top: '58px',
    right: '0',
    padding: '20px 60px',
    borderLeft: '1px solid #ccc',
    borderTop: '1px solid #ccc',
    borderBottom: '1px solid #ccc',
    backgroundColor: '#f9f9f9',
    '@media screen and (max-width: 1600px)': {
      top: '58px'
    },
    '@media screen and (width: 1280px)': {
      top: '58px !important'
    },
    '@media screen and (max-width: 1280px)': {
      top: '51px'
    },
    '@media screen and (max-width: 959px)': {
      top: '45px',
      padding: '15px 40px'
    }
  },
  hiddenNav3: {
    position: 'absolute',
    top: '59px',
    right: '0',
    padding: '20px 60px',
    borderLeft: '1px solid #ccc',
    borderBottom: '1px solid #ccc',
    backgroundColor: '#f9f9f9',
    '@media screen and (min-width: 1280px) and (max-width: 1600px)': {
      top: '59px'
    },
    '@media screen and (max-width: 1279px)': {
      top: '52px'
    },
    '@media screen and (max-width: 959px)': {
      top: '46px',
      padding: '15px 40px'
    }
  },
  mapNav: {
    gap: '6.4px'
  },
  dropdownArrow: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut
    })
  },
  dropdownArrowOpen: {
    transform: 'rotate(180deg)'
  },
  alink: {
    fontFamily: 'Arial, sans-serif',
    fontWeight: '400',
    color: 'black !important',
    lineHeight: '24px',
    fontSize: '14px',
    '&:hover': {
      color: '#ee1c25 !important',
      textDecoration: 'none !important'
    }
  },
  encompassText: {
    fontSize: '10px',
    fontWeight: 400,
    fontFamily: 'Arial, sans-serif',
    color: 'black !important',
    lineHeight: '36px'
  },
  logo: {
    width: '150px',
    marginLeft: '174px',
    '@media screen and (max-width: 959px)': {
      width: '116px',
      marginLeft: '0px',
      position: 'absolute',
      top: '0px',
      left: '50%',
      transform: 'translateX(-50%)'
    }
  }
}));

/**
 * A link to this site is written as a path, never as a full address.
 *
 * These used to be built from a hostname lookup that returned an `https://` address
 * for every case, localhost included. Locally the site is served over plain HTTP, so
 * every one of these links left the dev server for a port that answers nothing, and
 * the address bar had to be edited by hand to get back. A path is resolved by the
 * browser against wherever the page is already loaded, so it carries the right scheme,
 * host and port in local, staging and production without a table to keep in step.
 *
 * `Sign Out` was already written this way; the rest now match it. Only genuinely
 * external destinations — the Google Sites hubs — stay absolute.
 */
const links = [
  { name: 'Sales Hub', customUrl: 'https://sites.google.com/iloanrate.com/icentral/home/sales-hub' },
  { name: 'Closing Board', customUrl: '/closing-board/calendar' },
  // { name: 'Milestone Closing Board', customUrl: '/milestone-closing-board/calendar' },
  { name: 'Marketing Hub', customUrl: 'https://sites.google.com/iloanrate.com/icentral/home/marketing-hub' },
  { name: 'iGrow Academy', customUrl: 'https://sites.google.com/iloanrate.com/icentral/home/igrow-academy' },
  // { name: 'Marketing Archive', customUrl: 'https://sites.google.com/iloanrate.com/icentral/home/marketing-hub' },
  { name: 'Help Center', customUrl: ' https://sites.google.com/iloanrate.com/icentral/home/help-center' },
  { name: 'HR Benefits Portal', customUrl: ' https://sites.google.com/iloanrate.com/icentral/home/hr-benefits-portal' },
  { name: 'Directory', customUrl: '/settings/contacts' }
];

// Automations used to hang here as a collapsible pair of links. It is a tab of the
// Admin Console now, so everything administrative lives behind one entry.
const links2 = [
  { name: 'Admin Console', customUrl: '/settings/users' },
  { name: 'User Profile', customUrl: '/userprofile' },
  { name: 'Sign Out', customUrl: '/logout' },
  { name: 'Encompass:', customUrl: 'https://sites.google.com/iloanrate.com/icentral/home/marketing' }
];

const NavbarHome = () => {
  const classes = useStyles();
  const [isLeftOpen, setIsLeftOpen] = useState(false);
  const [isRightOpen, setIsRightOpen] = useState(false);
  const [isHamburgerMenu, setIsHamburgerMenu] = useState(true);
  const rightMenuTriggerRef = useRef(null);
  const rightMenuRef = useRef(null);

  useEffect(() => {
    if (!isRightOpen) {
      return undefined;
    }

    const handleClickOutside = event => {
      const clickedTrigger = rightMenuTriggerRef.current && rightMenuTriggerRef.current.contains(event.target);
      const clickedMenu = rightMenuRef.current && rightMenuRef.current.contains(event.target);

      if (!clickedTrigger && !clickedMenu) {
        setIsRightOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isRightOpen]);

  const user = useSelector(({ auth }) => auth.user);
  const userName = user.data.displayName;
  const { avatar } = user.data;

  // Two different questions used to share one `isAdmin`.
  //
  // Whether the Admin Console appears in this menu is a permission.
  const canOpenTheConsole = usePermission('admin.console.view');
  //
  // Whether the Closing Board link goes to the board or out to Encompass to sign in
  // first is not a question about rank — it is about whether this account has an
  // Encompass session, which `encompassId` just below is the real answer to. Kept on
  // a permission for now so nothing changes with this release; see the note at the
  // link itself.
  const goesStraightToTheBoard = usePermission('closingBoard.view.all');
  const encompassId = user.data.encompassId;

  const getInitials = userName =>
    userName
      .match(/(\b\S)?/g)
      .join('')
      .match(/(^\S|\S$)?/g)
      .join('')
      .toUpperCase();
  const getAvatarImg = avatar => {
    return <img className="max-w-none w-auto h-full" src={avatar} alt="avatar" />;
  };
  const avatarContent = avatar ? getAvatarImg(avatar) : getInitials(userName);

  const toggleLeftDropdown = () => {
    setIsLeftOpen(prev => !prev);
    setIsHamburgerMenu(!isHamburgerMenu);
    setIsRightOpen(false);
  };

  const toggleRightDropdown = () => {
    setIsRightOpen(prev => !prev);
    setIsLeftOpen(false);
    if (!isHamburgerMenu) {
      setIsHamburgerMenu(!isHamburgerMenu);
    }
  };

  return (
    <header className={clsx(classes.headernav, 'lg:pt-3 lg:pb-4 w-full')}>
      <div className="mx-20 md:mx-36">
        <div className="flex container items-center justify-between">
          <div>
            <div onClick={toggleLeftDropdown} className="cursor-pointer outline-none">
              <img
                className={clsx(classes.loginIcons)}
                src={
                  isHamburgerMenu
                    ? 'assets/images/icons/Hamburger_menu.svg'
                    : 'assets/images/icons/close-x-svgrepo-com.svg'
                }
                alt={isHamburgerMenu ? 'Hamburger menu' : 'Close menu'}
                style={{
                  transition: 'transform 0.4s ease, opacity 0.4s ease-in-out',
                  transform: isHamburgerMenu ? 'rotate(0deg)' : 'rotate(90deg)',
                  opacity: isHamburgerMenu ? 1 : 0.8
                }}
              />
            </div>
            <div
              className={clsx(
                classes.hiddenNav,
                `bg-white fixed z-20 transition-all duration-500 ease-in-out`,
                isLeftOpen ? 'opacity-100 translate-x-0 visible' : 'opacity-0 -translate-x-10 invisible'
              )}
            >
              <div className={clsx(classes.mapNav, 'flex flex-col')}>

                {links.map((link, index) => {
                  if (link.name === 'Closing Board') {
                    return (
                      <a
                        key={index}
                        className={clsx(classes.alink)}
                        href={
                          // About having an Encompass session, not about rank.
                          goesStraightToTheBoard
                            ? link.customUrl
                            : encompassAuthorizeUrl()
                        }
                      >
                        {link.name}
                      </a>
                    );
                  }

                  if (link.name === 'Milestone Closing Board') {
                    return (
                      <a
                        key={index}
                        className={clsx(classes.alink)}
                        href={
                          // About having an Encompass session, not about rank.
                          goesStraightToTheBoard
                            ? link.customUrl
                            : encompassAuthorizeUrl()
                        }
                      >
                        {link.name}
                      </a>
                    );
                  }

                  return (
                    <a key={index} className={clsx(classes.alink)} target="_self" href={link.customUrl}>
                      {link.name}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Logo central */}
          <div>
            <Link to="/menu/home-page">
              <img className={clsx(classes.logo)} src="assets/images/logos/icentral-logo.png" alt="logo" />
            </Link>
          </div>

          {/* Dropdown derecha */}
          <div className="flex gap-20 md:gap-16">
            <div className="flex gap-10 items-center">
              <InstallAppButton />
              <a href="https://sites.google.com/iloanrate.com/icentral/faq">
                <img className="w-20 lg:w-24" src="assets/images/icons/FAQ-icon.png" alt="FAQ" />
              </a>
              <img className="w-20 lg:w-24" src="assets/images/icons/notification.png" alt="Notifications" />
            </div>
            <div
              ref={rightMenuTriggerRef}
              onClick={toggleRightDropdown}
              className="flex gap-12 items-center cursor-pointer"
            >
              <Avatar className={clsx(classes.avatar, 'avatar')} alt="">
                {avatarContent}
              </Avatar>
              <div className="hidden md:block">
                <Typography className="username text-14 whitespace-no-wrap text-black font-800">
                  {user.data.displayName}
                </Typography>
                <Typography className="email text-12 whitespace-no-wrap text-black">
                  {user.data.iloanEmail || user.data.email}
                </Typography>
              </div>
              <img
                className={clsx(
                  classes.loginRowB,
                  classes.dropdownArrow,
                  isRightOpen && classes.dropdownArrowOpen,
                  'hidden sm:block'
                )}
                src="assets/images/icons/down-arrow.png"
                alt=""
                aria-hidden="true"
              />
            </div>
            <div
              ref={rightMenuRef}
              className={clsx(
                encompassId ? classes.hiddenNav2 : classes.hiddenNav3,
                `bg-white fixed z-20 transition-all duration-500 ease-in-out`,
                isRightOpen ? 'opacity-100 translate-x-0 visible' : 'opacity-0 translate-x-10 invisible'
              )}
            >
              <div className={clsx(classes.mapNav, 'flex flex-col')}>
                {links2.map((link, index) => {
                  if (link.name === 'Admin Console' && !canOpenTheConsole) {
                    return null;
                  }

                  if (link.name === 'Encompass:') {
                    return (
                      <React.Fragment key={index}>
                        <div className={clsx(classes.encompassText)}>
                          {`Encompass: ${encompassId ? 'Connected' : 'Disconnected'}`}
                        </div>
                      </React.Fragment>
                    );
                  }

                  return (
                    <React.Fragment key={index}>
                      <a className={clsx(classes.alink)} target="_self" href={link.customUrl}>
                        {link.name}
                        {link.name === 'Sign Out' && (
                          <img
                            className={clsx(classes.loginRow, 'ml-20 inline w-20 h-20')}
                            src="assets/images/icons/logout.svg"
                            alt="Logout Icon"
                          />
                        )}
                      </a>
                      {link.name === 'Sign Out' && <hr className="border-t border-black my-2" />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavbarHome;
